/**
 * @file Wave director (GDD §Fase 3 — oleadas).
 *
 * Replaces the phase-1 trickle spawner with discrete waves. Its own small state
 * machine: Preparing (breather) → Spawning (drip the roster in, each spawn
 * telegraphed) → Clearing (wait for the arena to empty) → Preparing.
 *
 * The roster comes from {@link ../config/Waves.js}, which spends a per-wave
 * point budget on unlocked archetypes. New enemies join by adding a cost and an
 * unlock wave; this file never changes.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import { AIBrain, RangedAI, PlayerControlled, Transform } from '../components/index.js';
import { createEnemy } from '../entities/Enemy.js';
import { rollWaveComposition, computeWaveBudget } from '../config/Waves.js';
import { getWaveTuning } from '../config/WaveTuning.js';
import { ENEMIES } from '../config/Enemies.js';
import { Events } from '../core/EventBus.js';
import { sandboxState } from '../sandbox/SandboxConfig.js';

/** Director phases. */
export const Phase = Object.freeze({
  PREPARING: 'preparing',
  SPAWNING: 'spawning',
  CLEARING: 'clearing'
});

export class WaveSystem extends System {
  static priority = 80;

  constructor() {
    super();
    /** @type {number} Current wave, 1-based. */
    this.wave = 1;
    /** @type {number} Total score. */
    this.score = 0;
    /** @type {number} Total kills. */
    this.kills = 0;

    /** @type {string} @private */
    this._phase = Phase.PREPARING;
    /** @type {number} Countdown for the current phase. @private */
    this._timer = 0;
    /** @type {string[]} Archetypes still to be released this wave. @private */
    this._roster = [];
    /**
     * Spawns that have been telegraphed and are waiting to materialise.
     * @type {Array<{ x: number, z: number, key: string, timer: number }>}
     * @private
     */
    this._pending = [];
    /**
     * The last roster {@link rollWaveComposition} produced, kept whole because
     * `_roster` is drained as the wave spawns. Read by the debug window to
     * contrast configured weights against what actually came out.
     * @type {string[]}
     * @private
     */
    this._lastComposition = [];
    /** @type {number} Budget the last roll had to spend. @private */
    this._lastBudget = 0;
    /** @type {{ x: number, z: number }} @private */
    this._point = { x: 0, z: 0 };
    /** @type {THREE.Vector3} @private */
    this._telegraphPosition = new THREE.Vector3();
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
  }

  /**
   * Wave table in force right now: the shipped balance, plus whatever the
   * debug window has edited. Read per use so an edit lands on the next roll.
   * @returns {typeof import('../config/Waves.js').WAVES}
   */
  get tuning() {
    return getWaveTuning();
  }

  /** @returns {string} Current director phase, one of {@link Phase}. */
  get phase() {
    return this._phase;
  }

  /**
   * How the director coexists with generator pre-population (issue #44):
   * 'prepopulated' (no waves), 'waves' (legacy), or 'both'. Falls back to
   * 'waves' when the dungeon config is absent, preserving old behaviour.
   * @returns {'prepopulated' | 'waves' | 'both'}
   */
  get mode() {
    return this.ctx?.config?.dungeon?.population?.mode ?? 'waves';
  }

  /** @returns {readonly string[]} Archetypes rolled for the current wave. */
  get lastComposition() {
    return this._lastComposition;
  }

  /** @returns {number} Point budget the current wave was rolled with. */
  get lastBudget() {
    return this._lastBudget;
  }

  /** @returns {number} Enemies rolled but not yet released. */
  get rosterRemaining() {
    return this._roster.length;
  }

  /** @returns {number} Spawns telegraphed and waiting to materialise. */
  get pendingCount() {
    return this._pending.length;
  }

  /** @returns {number} Current dungeon floor, used to unlock one new enemy per clear. */
  get levelIndex() {
    return Math.max(1, this.ctx.levelIndex ?? 1);
  }

  /** @returns {void} */
  init() {
    this.reset();
    this._subscriptions.push(
      this.ctx.events.on(Events.DIED, (payload) => this._onDeath(payload))
    );
  }

  /**
   * Restores the director to wave 1 with a clean score.
   * @returns {void}
   */
  reset() {
    this.wave = 1;
    this.score = 0;
    this.kills = 0;
    this._phase = Phase.PREPARING;
    this._timer = this.tuning.firstWaveDelay;
    this._roster.length = 0;
    this._pending.length = 0;
    this._lastComposition.length = 0;
    this._lastBudget = 0;
    this._emitScore();
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    const player = this._findPlayer();
    if (!player || !this.ctx.arena) return;

    this._updatePending(dt);

    // 'prepopulated': the generator already filled the dungeon; the director
    // stays dormant and progress is measured by clearing it / reaching the exit.
    if (this.mode === 'prepopulated') return;

    switch (this._phase) {
      case Phase.PREPARING:
        this._updatePreparing(dt);
        break;
      case Phase.SPAWNING:
        this._updateSpawning(dt, player);
        break;
      case Phase.CLEARING:
        this._updateClearing();
        break;
      default:
        break;
    }
  }

  /**
   * Materialises spawns whose warning ring has finished playing.
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   * @private
   */
  _updatePending(dt) {
    for (let index = this._pending.length - 1; index >= 0; index--) {
      const pending = this._pending[index];
      pending.timer -= dt;
      if (pending.timer > 0) continue;

      const enemy = createEnemy(this.ctx, pending.x, pending.z, pending.key);
      // Tag wave spawns so CLEARING waits only on this wave's own enemies, never
      // on a pre-placed enemy the player has not yet discovered (issue #44).
      enemy.fromWave = true;
      // Swap-remove: order does not matter here.
      this._pending[index] = this._pending[this._pending.length - 1];
      this._pending.pop();
    }
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   * @private
   */
  _updatePreparing(dt) {
    this._timer -= dt;
    if (this._timer > 0) return;

    const tuning = this.tuning;
    this._roster = rollWaveComposition(this.wave, tuning, this.levelIndex);
    this._lastComposition = [...this._roster];
    this._lastBudget = computeWaveBudget(this.wave, tuning);
    // Sandbox enemy-type toggles filter wave spawns while the panel is active.
    if (sandboxState.enabled) {
      this._roster = this._roster.filter((key) => sandboxState.enemyToggles[key] !== false);
    }
    this._phase = Phase.SPAWNING;
    this._timer = 0;
    this.ctx.events.emit(Events.WAVE_STARTED, {
      wave: this.wave,
      count: this._roster.length
    });
    this.ctx.events.emit(Events.SHAKE, { amount: this.ctx.config.feel.trauma.wave });
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @param {import('../ecs/Entity.js').Entity} player Player entity.
   * @returns {void}
   * @private
   */
  _updateSpawning(dt, player) {
    this._timer -= dt;
    if (this._timer > 0) return;

    if (this._roster.length === 0) {
      this._phase = Phase.CLEARING;
      return;
    }

    const tuning = this.tuning;
    // A full arena stalls the roster rather than dropping it.
    const alive =
      this.world.query(AIBrain).length +
      this.world.query(RangedAI).length +
      this._pending.length;
    if (alive >= tuning.maxAlive) {
      this._timer = tuning.spawnInterval;
      return;
    }

    const key = this._roster.pop();
    this._schedule(key, player);
    this._timer = tuning.spawnInterval;
  }

  /**
   * @returns {void}
   * @private
   */
  _updateClearing() {
    // Only this wave's own spawns gate the clear. In 'both' mode a pre-placed
    // enemy still lurking in an unexplored room must not stall the director.
    const waveAlive =
      this.world.query(AIBrain).reduce((n, e) => n + (e.fromWave ? 1 : 0), 0) +
      this.world.query(RangedAI).reduce((n, e) => n + (e.fromWave ? 1 : 0), 0);
    if (this._pending.length > 0 || waveAlive > 0) return;

    this.ctx.events.emit(Events.WAVE_CLEARED, { wave: this.wave });
    this.wave++;
    this._phase = Phase.PREPARING;
    this._timer = this.tuning.breather;
    this._emitScore();
  }

  /**
   * Picks a spawn point, announces it, and queues the enemy behind the warning.
   * @param {string} key Archetype key.
   * @param {import('../ecs/Entity.js').Entity} player Player entity.
   * @returns {void}
   * @private
   */
  _schedule(key, player) {
    const archetype = ENEMIES[key];
    const origin = player.get(Transform).position;
    const tuning = this.tuning;

    const placed = this.ctx.arena.findSpawnPoint(
      origin,
      tuning.minPlayerDistance,
      archetype.radius,
      this._point
    );
    // No clear spot this frame: put the entry back and try again next tick.
    if (!placed) {
      this._roster.push(key);
      return;
    }

    this._pending.push({
      x: this._point.x,
      z: this._point.z,
      key,
      timer: tuning.telegraphTime
    });

    this._telegraphPosition.set(this._point.x, 0.1, this._point.z);
    this.ctx.events.emit(Events.SPAWN_TELEGRAPH, {
      position: this._telegraphPosition,
      duration: tuning.telegraphTime
    });
  }

  /**
   * @returns {import('../ecs/Entity.js').Entity | null}
   * @private
   */
  _findPlayer() {
    const players = this.world.query(PlayerControlled, Transform);
    return players.length > 0 ? players[0] : null;
  }

  /**
   * @param {{ entity: import('../ecs/Entity.js').Entity }} payload Death event.
   * @returns {void}
   * @private
   */
  _onDeath({ entity }) {
    if (!entity.has(AIBrain) && !entity.has(RangedAI)) return;

    this.kills++;
    this.score += entity.scoreValue ?? 0;
    this._emitScore();
  }

  /** @returns {void} @private */
  _emitScore() {
    this.ctx.events.emit(Events.SCORE_CHANGED, {
      score: this.score,
      wave: this.wave,
      kills: this.kills
    });
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
  }
}
